// File Name: pages/terms-and-policy-pages/AppDeleteAccount.js
//
// Route: /app/delete-account
//
// This page exists because Google Play requires it. An app that lets people
// create accounts must offer deletion **inside the app** and at a **publicly
// reachable URL** that works without installing anything or signing in — this
// URL goes in the Play Console listing, and a reviewer will open it.
//
// So it is a real instruction page, not a form: the in-app route is the one
// that actually deletes, and email is the fallback for people who no longer
// have the app.

import React from "react";
import { Typography } from "antd";
import { Link } from "react-router-dom";
import LegalPageShell, { APP_LEGAL_CONTACT } from "./LegalPageShell";

const { Title, Paragraph, Text } = Typography;

const AppDeleteAccount = () => (
  <LegalPageShell
    title="YNO App — Delete Your Account"
    subtitle="How to permanently delete your YNO account and the data associated with it."
  >
    <Title level={4}>1. Deleting from inside the app (fastest)</Title>
    <Paragraph>If you still have the YNO app installed:</Paragraph>
    <ol>
      <li>Open the app and sign in</li>
      <li>
        Open the menu and go to <Text strong>Settings</Text>
      </li>
      <li>
        Tap <Text strong>Delete my account</Text>
      </li>
      <li>
        Read the warning. The confirm button unlocks after a short pause, which
        is there to prevent an accidental tap.
      </li>
      <li>
        We email a <Text strong>6-digit code</Text> to the address on your
        account. Enter it to confirm.
      </li>
    </ol>
    <Paragraph>
      The deletion happens immediately once the code is accepted, and you are
      signed out. <Text strong>It cannot be undone.</Text>
    </Paragraph>

    <Title level={4}>2. Deleting by email</Title>
    <Paragraph>
      If you no longer have the app installed, or cannot sign in, email{" "}
      <Text strong>{APP_LEGAL_CONTACT}</Text> from{" "}
      <Text strong>the email address registered on the account</Text>, with the
      subject <Text strong>"Delete my YNO account"</Text>.
    </Paragraph>
    <Paragraph>
      We will verify that the request comes from the account owner and then
      delete the account. We may ask one or two questions to confirm your
      identity first — this protects you from someone else deleting your account.
    </Paragraph>

    <Title level={4}>3. What is deleted</Title>
    <ul>
      <li>Your login and the ability to sign in</li>
      <li>
        Your profile: name, username, email, phone number, date of birth,
        gender, profile photo and social links
      </li>
      <li>
        Your football profile and career statistics: position, preferred foot,
        skill level, goals, assists, matches played, wins, losses and streaks
      </li>
      <li>Your friends, follows and pending invitations</li>
      <li>Your points and referral rewards</li>
      <li>Your notifications and your push notification tokens</li>
    </ul>

    <Title level={4}>4. What is kept, and why</Title>
    <Paragraph>
      <Text strong>Match records are not deleted.</Text> A match belongs to
      everyone who played in it, not only to you — deleting it would erase other
      players' history and their statistics along with yours. So a match will
      still show that it took place, its teams and its result.
    </Paragraph>
    <Paragraph>
      We may also keep limited records where the law requires it, or where we
      need them to resolve a dispute or prevent abuse.
    </Paragraph>

    <Title level={4}>5. Timing</Title>
    <Paragraph>
      Deletion from inside the app is immediate. Requests by email are actioned
      without undue delay, and in any case within 30 days. Backups are overwritten
      on a rolling cycle, so a copy may persist there for a short period after
      your account has gone from the live service.
    </Paragraph>

    <Title level={4}>6. If you only want a break</Title>
    <Paragraph>
      You do not have to delete anything. In <Text strong>Settings</Text> you can{" "}
      <Text strong>deactivate</Text> your account for a period you choose, up to
      one month. Your profile is hidden from other players, and everything comes
      back when you sign in again. That is reversible; deletion is not.
    </Paragraph>

    <Title level={4}>7. Questions</Title>
    <Paragraph>
      Email <Text strong>{APP_LEGAL_CONTACT}</Text>. See also our{" "}
      <Link to="/app/privacy">Privacy Policy</Link> and our{" "}
      <Link to="/app/terms">Terms &amp; Conditions</Link>.
    </Paragraph>
  </LegalPageShell>
);

export default AppDeleteAccount;
